import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  CircleAlert,
  KeyRound,
  LoaderCircle,
  Mail,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import { type FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { trackEvent } from "../analytics";
import { api } from "../api";
import { guestSetupPath, safeReturnTo } from "../authReturn";
import { BrandMark } from "../components/BrandMark";
import { LoadingButton } from "../components/LoadingButton";
import { OtpSafeInput, type OtpSafeState, otpUnlockDelay } from "../components/OtpSafeInput";
import { TurnstileWidget, type TurnstileWidgetHandle } from "../components/TurnstileWidget";
import { useAppStore } from "../state";
import { supabase } from "../supabase";
import { useVisitorContext } from "../visitorContext";

interface SignInPageProps {
  initialMode?: "magic" | "password" | "signup";
}

type MagicLinkStatus =
  | { phase: "checking"; message: string }
  | { phase: "opening"; message: string }
  | { phase: "error"; message: string };

function playerMagicLink(): { challengeId: string; token: string } | null {
  const search = new URLSearchParams(window.location.search);
  const challengeId = search.get("challenge");
  const token = search.get("token");
  if (!challengeId || !token || search.get("audience") !== "player") return null;
  return { challengeId, token };
}

export function SignInPage({ initialMode: _initialMode }: SignInPageProps) {
  const navigate = useNavigate();
  const guest = useAppStore((state) => state.guest);
  const challengeRef = useRef<TurnstileWidgetHandle>(null);
  const visitor = useVisitorContext();
  const [returnTo] = useState(() =>
    safeReturnTo(new URLSearchParams(window.location.search).get("returnTo")),
  );
  const [magicLink] = useState(playerMagicLink);
  const [magicStatus, setMagicStatus] = useState<MagicLinkStatus | null>(() =>
    magicLink ? { phase: "checking", message: "Checking your secure sign-in link…" } : null,
  );
  const [step, setStep] = useState<"identifier" | "code">("identifier");
  const [identifier, setIdentifier] = useState("");
  const [channel, setChannel] = useState<"email" | "phone" | null>(null);
  const [code, setCode] = useState("");
  const [otpState, setOtpState] = useState<OtpSafeState>("idle");
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState("");
  const [resendIn, setResendIn] = useState(0);
  const resendSeconds = Math.max(
    30,
    Number(import.meta.env.VITE_OTP_RESEND_INTERVAL_SECONDS ?? "60"),
  );
  const receiveChallenge = useCallback((token: string | null) => setCaptchaToken(token), []);

  const finishSignIn = useCallback(
    async (
      session: { access_token: string; refresh_token: string },
      method: "email_magic_link" | "email_otp" | "phone_otp",
      beforeNavigate?: () => Promise<void>,
    ) => {
      if (!supabase) throw new Error("Connect Supabase before signing in.");
      const { error } = await supabase.auth.setSession({
        access_token: session.access_token,
        refresh_token: session.refresh_token,
      });
      if (error) throw error;
      trackEvent("login", { method });
      await beforeNavigate?.();
      navigate(returnTo, { replace: true });
    },
    [navigate, returnTo],
  );

  useEffect(() => {
    if (!magicLink) return;
    window.history.replaceState(window.history.state, "", window.location.pathname);
    void api
      .verifyMagicLink({ ...magicLink, audience: "player" })
      .then(({ session }) => {
        setMagicStatus({
          phase: "opening",
          message: "Link confirmed. Opening your account…",
        });
        return finishSignIn(session, "email_magic_link");
      })
      .catch((error) => {
        setMagicStatus({
          phase: "error",
          message:
            error instanceof Error ? error.message : "This sign-in link is invalid or has expired.",
        });
      });
  }, [finishSignIn, magicLink]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = window.setInterval(
      () => setResendIn((current) => Math.max(0, current - 1)),
      1_000,
    );
    return () => window.clearInterval(timer);
  }, [resendIn]);

  const requestCode = async () => {
    const enteredValue = identifier.trim();
    const isEmail = enteredValue.includes("@");
    const digits = enteredValue.replace(/\D/g, "");
    const value =
      !isEmail && !enteredValue.startsWith("+") && visitor.callingCode
        ? `${visitor.callingCode}${digits}`
        : enteredValue;
    if (
      (isEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) ||
      (!isEmail && !/^\+[1-9]\d{7,14}$/.test(value.replace(/[\s()-]/g, "")))
    )
      throw new Error("Enter a valid email or a mobile number such as +919876543210.");
    if (!captchaToken) throw new Error("Complete the security check first.");
    const result = await api.requestAuth({
      identifier: value,
      audience: "player",
      turnstileToken: captchaToken,
      returnTo,
    });
    if (!isEmail && value !== identifier) setIdentifier(value);
    setStep("code");
    setChannel(result.channel);
    setCode("");
    setOtpState("idle");
    setResendIn(result.resendAfterSeconds || resendSeconds);
    setMessage(
      result.channel === "email"
        ? `We sent a magic link and a six-digit code to ${result.destination}.`
        : `We sent a six-digit code to ${result.destination}.`,
    );
    trackEvent("account_auth_code_requested", { method: result.channel });
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMessage("");
    setPending(true);
    try {
      if (step === "identifier") {
        await requestCode();
        return;
      }
      if (!/^\d{6}$/.test(code))
        throw new Error(
          `Enter the six-digit code from your ${channel === "email" ? "email" : "text message"}.`,
        );
      setOtpState("validating");
      const result = await api.verifyAuthOtp({
        identifier: identifier.trim(),
        audience: "player",
        code,
      });
      await finishSignIn(
        result.session,
        channel === "email" ? "email_otp" : "phone_otp",
        async () => {
          setOtpState("success");
          await otpUnlockDelay();
        },
      );
    } catch (error) {
      trackEvent("operation_failed", {
        operation: step === "identifier" ? "auth_code_request" : "auth_verify",
        route: "/sign-in",
      });
      setMessage(error instanceof Error ? error.message : "Sign-in failed. Please try again.");
      if (step === "code") setOtpState("error");
      if (step === "identifier") challengeRef.current?.reset();
    } finally {
      setPending(false);
    }
  };

  const resend = async () => {
    if (resendIn > 0 || pending) return;
    setPending(true);
    setMessage("");
    try {
      challengeRef.current?.reset();
      setStep("identifier");
      setMessage("Complete the fresh security check, then send a new code.");
    } finally {
      setPending(false);
    }
  };

  if (magicStatus) {
    const failed = magicStatus.phase === "error";
    return (
      <section className="magic-link-page" aria-live="polite">
        <div className={`magic-link-panel${failed ? " failed" : ""}`}>
          <BrandMark />
          <div className="magic-link-indicator" aria-hidden="true">
            {failed ? (
              <CircleAlert />
            ) : magicStatus.phase === "opening" ? (
              <CheckCircle2 />
            ) : (
              <LoaderCircle className="magic-link-spinner" />
            )}
          </div>
          <h1>
            {failed
              ? "This link didn’t work"
              : magicStatus.phase === "opening"
                ? "You’re signed in"
                : "Signing you in"}
          </h1>
          <p>{magicStatus.message}</p>
          {failed && (
            <a className="button button-primary" href="/sign-in">
              Request a new link
            </a>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="auth-page">
      <div className="auth-layout">
        <div className="auth-intro">
          <span className="eyebrow">ONE ACCOUNT · NO PASSWORD</span>
          <h2>Your games stay with you.</h2>
          <p>
            Use your email or mobile number to keep rooms, friends and results connected across
            devices. Other players never see either one.
          </p>
          <div className="auth-proof">
            <ShieldCheck />
            <div>
              <strong>Age-aware safety</strong>
              <small>
                Young players automatically get stricter privacy, chat and call controls.
              </small>
            </div>
          </div>
          <div className="auth-proof">
            <Mail />
            <div>
              <strong>No password to remember</strong>
              <small>Email gets a magic link and code. Mobile gets a text-message code.</small>
            </div>
          </div>
        </div>
        <form className="auth-card" onSubmit={(event) => void submit(event)}>
          <span className="icon-tile">{step === "identifier" ? <Mail /> : <KeyRound />}</span>
          <span className="eyebrow">
            {step === "identifier"
              ? "SIGN IN OR CREATE ACCOUNT"
              : channel === "email"
                ? "CHECK YOUR EMAIL"
                : "CHECK YOUR PHONE"}
          </span>
          <h1>{step === "identifier" ? "Let’s get you in." : "Enter your code."}</h1>
          <p>
            {step === "identifier"
              ? "Enter your email or mobile number. If you’re new, this creates your account automatically."
              : channel === "email"
                ? `Use the code sent to ${identifier}, or simply open the magic link in that email.`
                : `Use the one-time code sent to ${identifier}.`}
          </p>
          {step === "identifier" ? (
            <>
              <label>
                Email or mobile number
                <input
                  type="text"
                  inputMode="email"
                  autoComplete="username"
                  value={identifier}
                  onChange={(event) => setIdentifier(event.target.value.slice(0, 254))}
                  placeholder={`you@example.com or ${visitor.callingCode ?? "+"}…`}
                  disabled={pending}
                  required
                />
                {visitor.countryName && visitor.callingCode && (
                  <small>
                    Mobile numbers without a prefix use {visitor.callingCode} for{" "}
                    {visitor.countryName}. You can enter another country code at any time.
                  </small>
                )}
              </label>
              <TurnstileWidget
                ref={challengeRef}
                action="account_auth"
                onToken={receiveChallenge}
              />
            </>
          ) : (
            <label htmlFor="player-one-time-code">
              One-time code
              <OtpSafeInput
                id="player-one-time-code"
                value={code}
                onChange={(value) => {
                  setCode(value);
                  setOtpState("idle");
                }}
                state={otpState}
                disabled={pending}
              />
            </label>
          )}
          <LoadingButton
            type="submit"
            className="button button-primary wide"
            loading={pending}
            loadingLabel={step === "identifier" ? "Sending sign-in…" : "Checking code…"}
            disabled={pending || (step === "identifier" && !captchaToken)}
          >
            {step === "identifier" ? "Continue securely" : "Verify and continue"} <ArrowRight />
          </LoadingButton>
          <Link
            className="button button-ghost wide"
            to={guest ? returnTo : guestSetupPath(returnTo)}
          >
            <UserRound /> {guest ? "Continue as guest" : "Play anonymously"}
          </Link>
          {step === "code" && (
            <div className="otp-actions">
              <button
                type="button"
                className="text-link"
                onClick={() => {
                  setStep("identifier");
                  setChannel(null);
                  setCode("");
                  setOtpState("idle");
                  setMessage("");
                  challengeRef.current?.reset();
                }}
                disabled={pending}
              >
                <ArrowLeft /> Change email or number
              </button>
              <button
                type="button"
                className="text-link"
                disabled={pending || resendIn > 0}
                onClick={() => void resend()}
              >
                {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
              </button>
            </div>
          )}
          {message && (
            <div className="status-message" role="status">
              {message}
            </div>
          )}
          <small>
            By continuing, you agree to the <Link to="/terms">Terms</Link> and acknowledge the{" "}
            <Link to="/privacy">Privacy Policy</Link>.
          </small>
        </form>
      </div>
    </section>
  );
}
